import React from "react";
import styles from "./orders.module.css";
import LoadingSpinner from "../CommonComponents/loadingSpinner";
import AdvancedMultiCompanySelectorInput from "../CommonComponents/AdvancedMultiCompanySelectorInput/AdvancedMultiCompanySelectorInput";
import OrdersFiltersButton from "./OrdersFiltersButton";
import { getOrderStatusDisplayText } from "../../Enums/orderStatus";
import { parseDate, formatDate, DATE_FORMATS } from "../../Util/dateUtils";

const getStatusBadgeClass = (status) => {
  const statusClassMap = {
    "PICKED_UP": "statusBadgePickedUp",
    "STARTED": "statusBadgeStarted",
    "ON_THE_WAY": "statusBadgeOnTheWay",
    "READY_TO_DELIVER": "statusBadgeOnTheWay",
  };
  return statusClassMap[status] || "statusBadgeDefault";
};

const formatTimeOnly = (timestamp) => {
  if (!timestamp) return "—";
  try {
    const date = parseDate(timestamp);
    return formatDate(date, DATE_FORMATS.HHcMM_AP);
  } catch (e) {
    return "—";
  }
};

const SingleOrder = ({ order, onOrderClick }) => {
  const handleClick = () => {
    if (onOrderClick && order.getOrderId() && order.getCompanyId()) {
      onOrderClick(order.getOrderId(), order.getCompanyId());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const locationName = order.getRestaurant()?.getName() || "Location";
  const pickupTime = formatTimeOnly(order.getExpectedPickupTime());
  const deliveryTime = formatTimeOnly(order.getExpectedDeliveryTime());

  return (
    <div
      className={styles.orderCard}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Order #${order.getOrderNumber()}`}
    >
      <div className={styles.orderCardHeader}>
        <div className={styles.orderCardHeaderLeft}>
          <div className={`${styles.statusBadge} ${styles[getStatusBadgeClass(order.getStatus())]}`}>
            <span className={styles.statusText}>
              {getOrderStatusDisplayText(order.getStatus())}
            </span>
          </div>
        </div>
        <div className={styles.orderCardHeaderRight}>
          {order.getAssignedCarrierId() != null && (
            <div className={styles.driverPill}>
              <span className={styles.driverPillText}>{order.getAssignedCarrierName()}</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.orderCardBody}>
        <div className={styles.orderId}>#{order.getOrderNumber()}</div>
        <div className={styles.orderCardRows}>
          <div className={styles.orderCardRow}>
            <div className={styles.orderCardLabel}>{locationName}</div>
            <div className={styles.orderCardTime}>{pickupTime}</div>
          </div>
          <div className={styles.orderCardRow}>
            <div className={styles.orderCardValue}>
              {order.getCustomer()?.getName() || "—"}
            </div>
            <div className={styles.orderCardTime}>{deliveryTime}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrdersList = ({ orders, onOrderClick }) => {
  if (orders.length === 0) {
    return <div className={styles.emptyState}>No orders found</div>;
  }

  return (
    <div className={styles.listContent}>
      {orders.map((order) => (
        <SingleOrder
          key={order.getOrderId()}
          order={order}
          onOrderClick={onOrderClick}
        />
      ))}
    </div>
  );
};

const OrdersListContainer = ({ data, isLoading, error, isSuccess, page, onPageChange, onOrderClick, companies = [], setCompanies, statusFilters = [], setStatusFilters }) => {
  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.titleRow}>
            <div className={styles.title}>Orders</div>
          </div>
        </div>
      </div>

      {/* Company selector + filters button */}
      <div className={styles.countSection}>
        <div className={styles.filterRow}>
          <div className={styles.filterCompanyWrap}>
            <AdvancedMultiCompanySelectorInput
              companyIds={companies}
              setCompanyIds={setCompanies}
              additionalClasses={styles.filterCompanySelectorHeight}
            />
          </div>
          <OrdersFiltersButton
            statusFilters={statusFilters}
            setStatusFilters={setStatusFilters}
          />
        </div>
      </div>

      {/* Scrollable List Section */}
      <div className={styles.listContainer}>
        {isLoading && (
          <div className={styles.loadingState}>
            <LoadingSpinner />
          </div>
        )}
        
        {error && (
          <div className={styles.errorState}>
            Error loading orders: {error.message || "Unknown error"}
          </div>
        )}
        
        {isSuccess && !isLoading && data && (
          <OrdersList orders={data.getData()} onOrderClick={onOrderClick} />
        )}
      </div>

      {/* Pagination + count text at bottom, left padding clears floating button */}
      <div className={styles.countSectionBottom}>
        {isSuccess && data && data.getTotalPages() > 1 && (
          <div className={styles.paginationButtons}>
            <button
              className={styles.pageButton}
              onClick={() => onPageChange(data.getPageNumber() - 1)}
              disabled={data.getPageNumber() <= 1}
            >
              Previous Page
            </button>
            <button
              className={styles.pageButton}
              onClick={() => onPageChange(data.getPageNumber() + 1)}
              disabled={data.getPageNumber() >= data.getTotalPages()}
            >
              Next Page
            </button>
          </div>
        )}
        <div className={styles.countContent}>
          <div className={styles.countText}>
            {isLoading ? "Loading..." : error ? "Error" : isSuccess && data ? `${data.getData().length} / ${data.getTotalCount()} orders` : "0 orders"}
          </div>
          {isSuccess && data && (
            <div className={styles.pageText}>Page {data.getPageNumber()} / {data.getTotalPages()}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersListContainer;
